<div id="page-root" class="max-w-824">
  <section class="header">
    <section class="icon-wrapper">
      <i class="fa-light fa-circle-check"></i>
      <h1 class="heading-1">Thanks for booking</h1>
    </section>
    <p class="body mt-8 email-info">
      We’ve emailed you these details. You can also
      <button
        aria-label="download a copy"
        class="body bold email-info download"
        (click)="handleDownload()"
        data-testid="download-booking-confirmation"
      >
        download a copy</button
      >.
    </p>
  </section>

  @if (vm.confirmedBookingLoaded) {
    <section class="booking mt-32 sm-mt-24 mb-64 sm-mb-40">
      <section class="container">
        <ng-container>
          <section class="column col-1">
            <ng-container *ngTemplateOutlet="bookingBox"></ng-container>
            <ng-container *ngTemplateOutlet="repairerBox"></ng-container>
          </section>

          <section class="column col-2">
            <ng-container *ngTemplateOutlet="mmrBox"></ng-container>
            <ng-container *ngTemplateOutlet="nextStepsBox"></ng-container>
            <ng-container *ngTemplateOutlet="contactBox"></ng-container>
          </section>
        </ng-container>
      </section>
    </section>

    <ng-template #bookingBox>
      <div class="block-wrapper booking-box">
        <h2 class="heading-5">Your repair</h2>
        @for (items of vm.bookingSummaryItems; track items.label) {
          <p class="body bold mt-12">{{ items.label }}</p>
          @if (items.encrypt) {
            <p class="body mt-4" [attr.data-testid]="items.testId" qmEncrypt>
              {{ items.value }}
            </p>
          } @else {
            <p class="body mt-4" [attr.data-testid]="items.testId">
              {{ items.value }}
            </p>
          }
        }
      </div>
    </ng-template>

    <ng-template #repairerBox>
      <div class="block-wrapper repairer-box">
        <h2 class="heading-5">Who'll repair it</h2>

        <p class="body bold mt-12">Repairer</p>
        <p class="body mt-4" data-testid="repairer-name">{{ vm.serviceProvider }}</p>

        @if (vm.serviceProviderEmail) {
          <p class="body bold mt-12">Email</p>
          <section class="email-link" data-testid="repairer-email">
            <i class="fa-light fa-envelope"></i>
            <a [href]="'mailto:' + vm.serviceProviderEmail" class="link-text" data-testid="repairer-email">
              {{ vm.serviceProviderEmail }}
            </a>
          </section>
        }

        <p class="body bold mt-12">Phone</p>
        <app-telephone-link [phoneNumber]="vm.serviceProviderTelephone" data-testid="repairer-phone" class="mt-4" />
        <p class="call-cost hint mt-8" data-testid="call-cost-messaging">
          Calls cost the basic rate per minute plus your phone company’s access charge. From a mobile, they can cost
          much more. Calls to 0800 numbers are always free.
        </p>

        @if (vm.serviceProviderContactHours) {
          <div class="divider mt-16 mb-16 md-mt-16 md-mb-16"></div>

          <app-contact-hours [contactHours]="vm.serviceProviderContactHours" />
        }
      </div>
    </ng-template>

    <ng-template #mmrBox>
      <div class="block-wrapper mmr-box" data-testid="mmr-box">
        <h2 class="heading-5">Stay up to date</h2>

        <p class="body mt-8">You can check when your engineer will arrive and change your details.</p>

        <app-button
          dataLabel="mmr-button"
          class="md-ml-0 sm-full-width mt-16"
          [buttonOptions]="{
            variant: 'secondary',
            isLink: true,
            url: vm.mmrLink,
            target: '_blank',
          }"
          (onClick)="trackClick(); clearConfirmedBooking()"
          data-testid="manage-my-repair-btn"
        >
          Manage my repair
          <i class="fa-light fa-arrow-right ml-8"></i>
        </app-button>
      </div>
    </ng-template>

    <ng-template #nextStepsBox>
      <div class="block-wrapper next-steps-box" data-testid="next-steps-box">
        <h2 class="heading-5">What happens next?</h2>

        <p class="body bold mt-12" data-testid="next-step-title">
          <i class="fa-light fa-truck fa-lg mr-8" style="color: #8b56e7"></i>Arrange parking
        </p>
        <p class="body mt-4" data-testid="next-step-description">Remember to have a parking permit ready on the day.</p>

        <p class="body bold mt-12" data-testid="next-step-title">
          <i class="fa-light fa-universal-access fa-lg mr-8" style="color: #09788c"></i>Provide access
        </p>
        <p class="body mt-4" data-testid="next-step-description">
          Before the repair, unplug your appliance and make it easy to reach. (Also defrost it, if it's a freezer.)
        </p>

        <p class="body bold mt-12" data-testid="next-step-title">
          <i class="fa-light fa-envelope fa-lg mr-8" style="color: #ce7e07"></i>Repair updates
        </p>
        <p class="body mt-4" data-testid="next-step-description">
          You’ll get a booking confirmation by email, and text reminders before the repair.
        </p>
      </div>
    </ng-template>

    <ng-template #contactBox>
      <div class="block-wrapper contact-box">
        <h2 class="heading-5">Your contact details</h2>

        <p class="body bold mt-12">Address</p>
        <section class="flex-col mt-4 address">
          @for (line of vm.address; track line) {
            <p qmBlock class="body address-line" data-testid="customer-address">
              {{ line }}
            </p>
          }
        </section>

        @if (vm.phoneNumber) {
          <p class="body bold mt-12">Phone</p>
          <p class="body mt-4" qmBlock data-testid="customer-phone">{{ vm.phoneNumber }}</p>
        }

        @if (vm.email) {
          <p class="body bold mt-12">Email</p>
          <p class="body mt-4" qmBlock data-testid="customer-email">{{ vm.email }}</p>
        }
      </div>
    </ng-template>
  } @else {
    <section class="skeleton-loader mt-32 sm-mt-24 mb-64 sm-mb-40">
      <app-skeleton-loader class="fill mt-40 h-32"> </app-skeleton-loader>
      <app-skeleton-loader class="fill mt-12 max-w-320 h-32"> </app-skeleton-loader>

      <app-skeleton-loader class="fill mt-20" />

      <app-skeleton-loader class="mt-20 fill p-16">
        <app-skeleton-loader class="fill max-w-240 h-28" />
        <app-skeleton-loader class="fill mt-12" />

        <section class="flex full-width gap-12" style="flex: 1">
          <app-skeleton-loader class="mt-20 h-60" style="flex: 1" />
          <app-skeleton-loader class="mt-20 h-60" style="flex: 1" />
        </section>

        <section class="flex full-width gap-12" style="flex: 1; justify-content: space-between">
          <app-skeleton-loader class="mt-20 h-52 max-w-160" style="flex: 1" />
          <app-skeleton-loader class="mt-20 h-52 max-w-160" style="flex: 1" />
        </section>
      </app-skeleton-loader>
    </section>
  }
</div>

<section style="position: absolute; top: 0; left: 0; z-index: -1" id="copy-content"></section>
